import React from "react";
import styled from "styled-components";
import { withStyles, makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";

import { get_subscriber_num_log } from "../../actions/vtuber";
import { useDispatch, useSelector } from "react-redux";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Label,
} from "recharts";

import Wrapper from "../atoms/Wrapper";
import { useMediaQuery } from "react-responsive";

const OuterWrapper = styled(Wrapper)`
  flex-direction: column;
  margin: 1rem 0 3rem 0;
  overflow: scroll;
  align-items: start;
`;
const InnerWrapper = styled(Wrapper)`
  flex-direction: column;
  padding: 1rem;
`;

const TableWrapper = styled(Wrapper)`
  margin-bottom: 2rem;
`;

const MobileStyledTableCell = withStyles((theme) => ({
  head: {
    color: theme.palette.colors.primary,
    fontWeight: "bold",
    background: theme.palette.colors.white,
    fontSize: 11,
  },
  body: {
    fontSize: 11,
  },
  footer: {
    color: theme.palette.colors.black,
    fontSize: 11,
  },
}))(TableCell);

const StyledTableCell = withStyles((theme) => ({
  head: {
    color: theme.palette.colors.primary,
    fontWeight: "bold",
    background: theme.palette.colors.white,
    fontSize: 14,
  },
  body: {
    fontSize: 14,
  },
  footer: {
    color: theme.palette.colors.black,
    fontSize: 14,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    background: theme.palette.colors.white,
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.colors.gray100,
    },
  },
}))(TableRow);

const useStyles = makeStyles({
  table: {
    width: 500,
    maxWidth: "100vw",
  },
});

export default function SubscriberNumLogTable({ vtuber_id }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  React.useEffect(() => {
    dispatch(get_subscriber_num_log(vtuber_id));
  }, [dispatch, vtuber_id]);

  const { items } = useSelector((state) => state.vtuber.subscriber_num_log);

  const add_plus = (n) => (n > 0 ? "+" : "");
  const item2diff = (items, item, idx) =>
    idx === items.length - 1
      ? ""
      : add_plus(item.subscriber_num - items[idx + 1].subscriber_num) +
        (item.subscriber_num - items[idx + 1].subscriber_num).toLocaleString() +
        "人";
  const item2diff_ratio = (items, item, idx) => {
    if (idx === items.length - 1) {
      return "";
    }

    const diff = item.subscriber_num - items[idx + 1].subscriber_num;
    const ratio = (diff / items[idx + 1].subscriber_num) * 100;
    return add_plus(ratio) + ratio.toFixed(2) + "%";
  };

  const get_week_start_idx = (items, n_weeks) =>
    items.length > 7 * n_weeks ? 7 * n_weeks : items.length - 1;

  const items2week_diff = (items, n_weeks) => {
    const start_idx = get_week_start_idx(items, n_weeks);
    const end_idx = 0;
    const diff =
      items[end_idx].subscriber_num - items[start_idx].subscriber_num;

    return add_plus(diff) + diff.toLocaleString() + "人";
  };

  const items2week_diff_ratio = (items, n_weeks) => {
    const start_idx = get_week_start_idx(items, n_weeks);
    const end_idx = 0;
    const diff =
      items[end_idx].subscriber_num - items[start_idx].subscriber_num;
    const ratio = (diff / items[start_idx].subscriber_num) * 100;

    return add_plus(ratio) + ratio.toFixed(2) + "%";
  };

  const n_weeks = items && Math.floor(items.length / 8);

  const is_under_800 = useMediaQuery({ query: "(max-width: 800px)" });
  const is_under_550 = useMediaQuery({ query: "(max-width: 550px)" });
  const is_under_400 = useMediaQuery({ query: "(max-width: 400px)" });
  let line_chart_width = 800;
  let line_chart_height = 500;

  if (is_under_400) {
    line_chart_width = 350;
    line_chart_height = 350;
  } else if (is_under_550) {
    line_chart_width = 400;
    line_chart_height = 400;
  } else if (is_under_800) {
    line_chart_width = 550;
    line_chart_height = 450;
  }

  const yaxis_range = 2;

  const calc_yaxis_min = (datamin) => {
    if (datamin <= yaxis_range) {
      return 0;
    }

    return (Math.floor(datamin / yaxis_range) - 1) * yaxis_range;
  };
  const calc_yaxis_max = (datamax) => {
    if (datamax <= yaxis_range) {
      return 0;
    }

    return (Math.floor(datamax / yaxis_range) + 2) * yaxis_range;
  };

  const TableCellComponent = is_under_550
    ? MobileStyledTableCell
    : StyledTableCell;

  return (
    <OuterWrapper>
      <InnerWrapper>
        {items && (
          <LineChart
            width={line_chart_width}
            height={line_chart_height}
            data={[...items].reverse().map((item) => ({
              日時: new Date(item.created_at).toLocaleString("ja-JP", {
                year: "numeric",
                month: "numeric",
                day: "numeric",
              }),
              "登録者数[万人]": item.subscriber_num / 10000.0,
            }))}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 70,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="日時">
              <Label value="日時" offset={-30} position="insideBottom" />
            </XAxis>
            <YAxis domain={[calc_yaxis_min, calc_yaxis_max]}>
              <Label value="登録者数[万人]" position="insideLeft" angle={-90} />
            </YAxis>
            <Tooltip />
            <Line
              type="monotone"
              dataKey="登録者数[万人]"
              stroke="#2089f1"
              dot={false}
            />
          </LineChart>
        )}
        {items && n_weeks >= 1 && (
          <TableWrapper>
            <TableContainer component={Paper}>
              <Table className={classes.table} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <TableCellComponent>期間</TableCellComponent>
                    <TableCellComponent align="right">
                      元登録者数
                    </TableCellComponent>
                    <TableCellComponent align="right">変動</TableCellComponent>
                    <TableCellComponent align="right">
                      変動割合
                    </TableCellComponent>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(n_weeks).keys()].map((i) => (
                    <StyledTableRow>
                      <TableCellComponent>{i + 1}週間</TableCellComponent>
                      <TableCellComponent align="right">
                        {items &&
                          items[0] &&
                          items[
                            get_week_start_idx(items, i + 1)
                          ].subscriber_num.toLocaleString()}
                        人
                      </TableCellComponent>
                      <TableCellComponent align="right">
                        {items && items[0] && items2week_diff(items, i + 1)}
                      </TableCellComponent>
                      <TableCellComponent align="right">
                        {items &&
                          items[0] &&
                          items2week_diff_ratio(items, i + 1)}
                      </TableCellComponent>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TableWrapper>
        )}

        <TableWrapper>
          <TableContainer component={Paper}>
            <Table className={classes.table} aria-label="customized table">
              <TableHead>
                <TableRow>
                  <TableCellComponent>日時</TableCellComponent>
                  <TableCellComponent align="right">
                    登録者数
                  </TableCellComponent>
                  <TableCellComponent align="right">変動</TableCellComponent>
                  <TableCellComponent align="right">
                    変動割合
                  </TableCellComponent>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => (
                  <StyledTableRow key={item.id}>
                    <TableCellComponent component="th" scope="row">
                      {new Date(item.created_at).toLocaleString("ja-JP", {
                        year: "numeric",
                        month: "numeric",
                        day: "numeric",
                      })}
                    </TableCellComponent>
                    <TableCellComponent align="right">
                      {item.subscriber_num.toLocaleString()}人
                    </TableCellComponent>
                    <TableCellComponent align="right">
                      {item2diff(items, item, idx)}
                    </TableCellComponent>
                    <TableCellComponent align="right">
                      {item2diff_ratio(items, item, idx)}
                    </TableCellComponent>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TableWrapper>
      </InnerWrapper>
    </OuterWrapper>
  );
}
